import React from "react";
import Modal from "components/Modal";
import { useAppDispatch, useAppSelector } from "store";
import { hideSnackbar } from "store/snackbarSlice";
import { useNavigate } from "react-router-dom";
import { AnalyticsEvent } from "utils";
import { IconClose, IconWarning } from "icons";
import { EventResetPhotoshoot } from "pages/PhotoshootPage/constants";
import { PATHS } from "router/config/paths";
import { resetSnackbar, setSnackbar, SnackbarType } from "store/commonSlice";
import ContactUsModal from "components/ContactUsModal";
import PaywallModal from "./PaywallModal";
import EndTrialModal from "./EndTrialModal";
import PastDueModal from "./PastDueModal";
import ContactAccountManagerModal from "./ContactAccountManagerModal";
import MemberCreditLimitModal from "./MemberCreditLimitModal";
import { ScreenType } from "./WarningSnackbar.types";
import {
  getText,
  getScreenTypeText,
  isDemoUserBlockedForScreen,
  getPackageTypeFromPopupType,
  getPaywallContentForTargetPackage,
  getGenericPaywallContent,
  getPaywallImage,
} from "./WarningSnackbar.utils";
import { useSiteImageResolver } from "hooks/useSiteImage";
import { isFakeEnterprisePackage } from "types/permissions";

// First URL segment (pathname.split("/")[1]) of the surfaces where a 402 credit
// wall can appear. Must include the actual credit-burning routes — `design-canvas`
// (Design Studio), `canvas-projects`, `photoshoots`/`photoshoot-studio` were missing,
// so the Upgrade_Plan_Pop-up event never fired where most credits are spent.
const AnalyticsPanels = ["design", "design-canvas", "canvas-projects", "new-training", "photoshoot", "photoshoots", "photoshoot-studio", "account", "trends"];

const photoshootScreenTypes = [ScreenType.Mannequin, ScreenType.Ghost, ScreenType.Tryon, ScreenType.Marketplace];
// Remedies that are not upgrades: a payment problem does not replace them (P7 Faz 1, below).
const POPUPS_WITH_OWN_REMEDY = ["member_credit_limit", "contact_account_manager"];

const designScreenTypes = [ScreenType.Design, ScreenType.Train, ScreenType.Team, ScreenType.Train, ScreenType.Edit];

const WarningSnackbar = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { visible, type, message, screen, panel, messageType, popupType, popupReason, requiredCredits, remainingCredits } = useAppSelector((state) => state.snackbar);
  const { user } = useAppSelector((state) => state.common);
  const { packageList } = useAppSelector((state) => state.credit);
  const clearedPathname = window.location.pathname.split("/")[1];
  const [showContactUsModal, setShowContactUsModal] = React.useState(false);
  const resolveSiteImage = useSiteImageResolver();

  const isDemoBlocked = React.useMemo(() => {
    return isDemoUserBlockedForScreen(user.demoInfo, screen);
  }, [user.demoInfo, screen]);

  React.useEffect(() => {
    // contact_account_manager and member_credit_limit ride the 402 popup machinery but are NOT
    // upgrade paywalls (no upgrade CTA) — exclude them from the Upgrade_Plan_Pop-up count so they
    // don't pollute Yigit's conversion metrics.
    const nonPaywallPopups = ["contact_account_manager", "member_credit_limit"];
    if (visible && AnalyticsPanels.includes(clearedPathname) && type === 402 && !nonPaywallPopups.includes(popupType)) {
      AnalyticsEvent("Upgrade_Plan_Pop-up", "", "pop-up shown", { panel: panel !== "" ? panel : getScreenTypeText(screen) });
    }
  }, [visible]);

  const handleClose = () => {
    dispatch(resetSnackbar());
    dispatch(hideSnackbar());
    setShowContactUsModal(false);

    if (type === 402) {
      const pathname = window.location.pathname;
      if ([ScreenType.Tryon, ScreenType.Mannequin, ScreenType.Ghost].includes(screen) || pathname.includes("photoshoot")) {
        navigate("/photoshoot", { replace: true, state: { photoshootType: null } });
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(EventResetPhotoshoot));
        }, 100);
      } else if ([ScreenType.Design, ScreenType.Train].includes(screen) || pathname.includes("design")) {
        navigate("/design", { replace: true });
      }
    }
  };

  const handleDismiss = () => {
    dispatch(resetSnackbar());
    dispatch(hideSnackbar());
    setShowContactUsModal(false);
  };

  const handleContactUsClose = () => {
    setShowContactUsModal(false);
    handleClose();
  };

  // Show Contact Us modal for demo users who are blocked
  React.useEffect(() => {
    if (visible && type === 402 && isDemoBlocked) {
      setShowContactUsModal(true);
    }
  }, [visible, type, isDemoBlocked]);

  // Show corner snackbar for messageType === "message" and hide the modal
  React.useEffect(() => {
    if (visible && type === 402 && messageType === "message" && message) {
      dispatch(
        setSnackbar({
          message: message,
          type: SnackbarType.Warning,
          icon: IconWarning,
          persistent: true,
        })
      );
      dispatch(hideSnackbar());
    }
  }, [visible, type, messageType, message, dispatch]);

  // For messageType === "message", don't show modal
  if (type === 402 && messageType === "message") {
    return null;
  }

  // D2 (STRIPE Phase 3): a payment problem is answered with PAYMENT RECOVERY before any other override — never a
  // Contact-Us or upgrade wall in its place.
  if (type === 402 && popupType === "past_due" && visible) {
    return <PastDueModal onClose={handleClose} requiredCredits={requiredCredits} remainingCredits={remainingCredits} reason={popupReason} />;
  }

  // P7 Faz 1 (Kardem): any other 402 popup — an upgrade wall naming a plan, contact-sales, end-trial, an unknown plan — for
  // a customer whose plan payment failed is answered with payment recovery too: buying another plan or calling sales cannot
  // fix a failed payment. A member credit cap and an account-manager instruction keep their own meaning.
  if (type === 402 && visible && user.hasPaymentProblem && !POPUPS_WITH_OWN_REMEDY.includes(popupType)) {
    return <PastDueModal onClose={handleClose} requiredCredits={requiredCredits} remainingCredits={remainingCredits} reason={popupReason} />;
  }

  // For demo users blocked for a feature, show Contact Us modal
  if (type === 402 && isDemoBlocked && visible) {
    return <ContactUsModal show={showContactUsModal} onClose={handleContactUsClose} type={getScreenTypeText(screen) || "demo"} />;
  }

  if (type === 402 && isFakeEnterprisePackage(user.packageCode) && visible) {
    return <ContactUsModal show={true} onClose={handleClose} type="enterprise" />;
  }

  // For popup_type === "contact_form" or "contact_us", show Contact Us modal
  if (type === 402 && (popupType === "contact_form" || popupType === "contact_us") && visible) {
    return <ContactUsModal show={true} onClose={handleClose} type={getScreenTypeText(screen) || "feature"} />;
  }

  if (type === 402 && popupType === "end_trial" && visible) {
    return <EndTrialModal onClose={handleClose} requiredCredits={requiredCredits} remainingCredits={remainingCredits} />;
  }

  // REF-5549 (BE REF-5446): enterprise/custom self-serve seat attempts return action
  // "contact_account_manager". Dispatched via showSnackbar with type=402 (reuses the global
  // popup machinery); the analytics effect above excludes it from the Upgrade_Plan_Pop-up count.
  if (type === 402 && popupType === "contact_account_manager" && visible) {
    return <ContactAccountManagerModal onClose={handleClose} />;
  }

  // REF-5549 (BE REF-5446): member hit the admin-set credit cap. No upgrade path for them —
  // CreditShortfallInfo + a dismiss button, telling them to ask their admin.
  if (type === 402 && popupType === "member_credit_limit" && visible) {
    return <MemberCreditLimitModal onClose={handleClose} requiredCredits={requiredCredits} remainingCredits={remainingCredits} />;
  }

  // Resolve paywall content from backend popupType. A popup_type naming no known package gets the plan-neutral paywall
  // (P7 Faz 1) — it used to render nothing.
  const targetPackage = popupType ? getPackageTypeFromPopupType(popupType) : null;
  // The paywall picture is a backoffice slot; the utils (non-React) return its bundled fallback, resolved here.
  const paywallImage = getPaywallImage(screen);
  const paywallContent = targetPackage !== null ? getPaywallContentForTargetPackage(targetPackage, screen, packageList, popupReason) : getGenericPaywallContent(screen, popupReason);
  const targetPaywallContent = { ...paywallContent, img: resolveSiteImage(paywallImage.slotKey, paywallImage.src) };

  if (!visible) return null;

  if (type === 402 && targetPaywallContent) {
    return (
      <PaywallModal
        content={targetPaywallContent}
        requiredCredits={requiredCredits}
        remainingCredits={remainingCredits}
        onClose={handleClose}
        onCancel={handleDismiss}
        onUpgrade={() => {
          handleClose();
          navigate(PATHS.PRICING, {
            state: {
              type: designScreenTypes.includes(screen) ? "design" : "photoshoot",
            },
          });
          AnalyticsEvent("Upgrade_Plan", "", "click", { panel: panel !== "" ? panel : getScreenTypeText(screen) });
        }}
        isNewPurchase={false}
      />
    );
  }

  // Fallback for non-402 errors (409, 503, etc.)
  return (
    <Modal onClose={handleClose} show={true} bodyClassName="bg-black-100 !h-auto !max-w-[1180px]" className="!z-[99999]" backdropDisabled={true}>
      <IconClose className="absolute cursor-pointer w-6 h-6 text-grey top-2 left-2" onClick={handleClose} />
      <div className="flex h-full gap-10 p-6 w-fit">
        <h2 className="text-h2 text-white h-fit text-center max-w-[650px]">{message ? message : getText(type)}</h2>
      </div>
    </Modal>
  );
};

export default WarningSnackbar;
